import { useState, useEffect, useRef, useCallback } from "react";

const API_URL = "https://dogecross-backend-production.up.railway.app";
const SITE_URL = "https://www.dogecross.com";

// ============================================================
// GAME CONFIG
// ============================================================
const COLS = 9;
const TILE_W = 56;
const ROAD_H = 480;        // tall road for mobile
const CANVAS_W = (COLS + 1) * TILE_W;
const CANVAS_H = ROAD_H + 60; // road + sidewalk strips
const TILE_H = 80;

// Multiplier per safe lane crossed — escalating risk/reward
const LANE_MULTS = [1.4, 1.9, 2.6, 3.5, 4.8, 6.5, 9.0, 12.0, 16.0];

// Car danger per lane (probability of car being there, baked server-side)
// Frontend just shows what server reveals

const C = {
  green:   "#39ff14",
  pink:    "#ff2d7b",
  cyan:    "#00f0ff",
  yellow:  "#ffe600",
  orange:  "#ff8c00",
  red:     "#ff003c",
  gold:    "#c2a633",
  goldLt:  "#e8d56b",
  bg:      "#0a0a12",
  panel:   "#12121f",
  purple:  "#b44aff",
  road:    "#1a1a1a",
  grass:   "#0d2a0d",
  stripe:  "#ffe600",
};

// ============================================================
// SOUND EFFECTS
// ============================================================
let audioCtx = null;
function getAC() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

const SFX = {
  step() {
    try {
      const ctx = getAC(), o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "square"; o.frequency.value = 300;
      g.gain.setValueAtTime(0.08, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.07);
      o.start(); o.stop(ctx.currentTime + 0.07);
    } catch {}
  },
  safe() {
    try {
      const ctx = getAC();
      [523, 659].forEach((f, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = f;
        g.gain.setValueAtTime(0, ctx.currentTime + i * 0.07);
        g.gain.linearRampToValueAtTime(0.12, ctx.currentTime + i * 0.07 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.07 + 0.18);
        o.start(ctx.currentTime + i * 0.07); o.stop(ctx.currentTime + i * 0.07 + 0.18);
      });
    } catch {}
  },
  bust() {
    try {
      const ctx = getAC(), o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sawtooth";
      o.frequency.setValueAtTime(400, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.5);
      g.gain.setValueAtTime(0.25, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
      o.start(); o.stop(ctx.currentTime + 0.55);
    } catch {}
  },
  cashOut() {
    try {
      const ctx = getAC();
      [523, 659, 784, 1047].forEach((f, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = f;
        g.gain.setValueAtTime(0, ctx.currentTime + i * 0.09);
        g.gain.linearRampToValueAtTime(0.13, ctx.currentTime + i * 0.09 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.09 + 0.22);
        o.start(ctx.currentTime + i * 0.09); o.stop(ctx.currentTime + i * 0.09 + 0.22);
      });
    } catch {}
  },
  gameStart() {
    try {
      const ctx = getAC();
      [262, 330, 392, 523].forEach((f, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "triangle"; o.frequency.value = f;
        g.gain.setValueAtTime(0, ctx.currentTime + i * 0.08);
        g.gain.linearRampToValueAtTime(0.1, ctx.currentTime + i * 0.08 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.08 + 0.15);
        o.start(ctx.currentTime + i * 0.08); o.stop(ctx.currentTime + i * 0.08 + 0.15);
      });
    } catch {}
  },
  honk() {
    try {
      const ctx = getAC(), o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sawtooth"; o.frequency.value = 220;
      g.gain.setValueAtTime(0.15, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      o.start(); o.stop(ctx.currentTime + 0.2);
    } catch {}
  },
};

// ============================================================
// API HELPER
// ============================================================
async function api(path, opts = {}) {
  const token = localStorage.getItem("dc_token");
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...opts.headers },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "API error");
  return data;
}

function Glow({ children, color = C.green, size = 14, style = {} }) {
  return <span style={{ color, fontSize: size, fontFamily: "'Press Start 2P', monospace", textShadow: `0 0 6px ${color}88, 0 0 18px ${color}44`, ...style }}>{children}</span>;
}

// ============================================================
// CANVAS DRAWING — landscape road, top-down view
// ============================================================
const SIDEWALK_H = 30;
const ROAD_TOP = SIDEWALK_H;
const LANE_COLORS_ALT = ["#7c4dbb", "#6a3fa8"];

function drawRoundRect(ctx, x, y, w, h, r, fill, stroke) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  if (fill) { ctx.fillStyle = fill; ctx.fill(); }
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 2; ctx.stroke(); }
}

function drawTaxi(ctx, lx, ry, rh) {
  const bw = TILE_W - 10, bh = rh * 0.48;
  const bx = lx + 4, by = ry + (rh - bh) / 2;
  const grad = ctx.createLinearGradient(bx, by, bx + bw, by);
  grad.addColorStop(0, "#f5c800"); grad.addColorStop(0.5, "#ffe033"); grad.addColorStop(1, "#f5c800");
  drawRoundRect(ctx, bx, by, bw, bh, 6, null, null);
  ctx.fillStyle = grad; ctx.fill();
  ctx.strokeStyle = "#b89000"; ctx.lineWidth = 2; ctx.stroke();
  // Checkered stripe
  const stripeH = 10, stripeY = by + bh / 2 - stripeH / 2;
  ctx.save(); ctx.beginPath(); ctx.rect(bx, stripeY, bw, stripeH); ctx.clip();
  for (let cx2 = bx; cx2 < bx + bw; cx2 += 8) {
    ctx.fillStyle = Math.floor((cx2 - bx) / 8) % 2 === 0 ? "#000" : "#fff";
    ctx.fillRect(cx2, stripeY, 8, stripeH);
  }
  ctx.restore();
  drawRoundRect(ctx, bx + 5, by + 6, bw - 10, bh * 0.22, 3, "#88aaee99", null);
  drawRoundRect(ctx, bx + 5, by + bh - bh * 0.28 - 4, bw - 10, bh * 0.22, 3, "#88aaee99", null);
  ctx.fillStyle = "#ff3333";
  ctx.fillRect(bx + 3, by + 2, 8, 5); ctx.fillRect(bx + bw - 11, by + 2, 8, 5);
  ctx.fillStyle = "#ffff44";
  ctx.fillRect(bx + 3, by + bh - 7, 8, 5); ctx.fillRect(bx + bw - 11, by + bh - 7, 8, 5);
  drawRoundRect(ctx, bx + bw/2 - 14, by - 14, 28, 12, 3, "#000", "#ffcc00");
  ctx.fillStyle = "#ffcc00"; ctx.font = "bold 8px monospace"; ctx.textAlign = "center";
  ctx.fillText("TAXI", bx + bw / 2, by - 5);
}

function drawCar(ctx, lx, ry, rh) {
  const bw = TILE_W - 14, bh = rh * 0.55;
  const bx = lx + 7, by = ry + (rh - bh) / 2;
  const grad = ctx.createLinearGradient(bx, by, bx + bw, by);
  grad.addColorStop(0, "#e8a0c0");
  grad.addColorStop(0.5, "#ffcce0");
  grad.addColorStop(1, "#e8a0c0");
  drawRoundRect(ctx, bx, by, bw, bh, 8, null, null);
  ctx.fillStyle = grad; ctx.fill();
  ctx.strokeStyle = "#c060a0"; ctx.lineWidth = 2; ctx.stroke();
  ctx.fillStyle = "#6688ccaa";
  drawRoundRect(ctx, bx + 5, by + bh * 0.25, bw - 10, bh * 0.35, 3, "#88aaee88", null);
  ctx.fillStyle = "#ff4444";
  ctx.fillRect(bx + 3, by + 2, bw - 6, 5);
  ctx.fillStyle = "#ffee44";
  ctx.fillRect(bx + 3, by + bh - 7, bw - 6, 5);
}

function drawChicken(ctx, x, cy, bust) {
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = bust ? "64px serif" : "52px serif";
  ctx.fillText(bust ? "💥" : "🐓", x, cy);
  ctx.textBaseline = "alphabetic";
}

function drawMultTile(ctx, cx, cy, mult, revealed, active) {
  const tw = TILE_W - 8, th = 48;
  const tx = cx - tw / 2, ty = cy - th / 2;
  if (!revealed) {
    drawRoundRect(ctx, tx, ty, tw, th, 8, "#2a1a4a", "#5a3a8a");
    ctx.fillStyle = "#ffffff44"; ctx.font = "bold 14px monospace"; ctx.textAlign = "center";
    ctx.fillText("?", cx, cy + 6);
    return;
  }
  const bg = active ? "#39ff14" : "#e8e0f8";
  const fg = active ? "#003300" : "#2a1060";
  drawRoundRect(ctx, tx, ty, tw, th, 8, bg, active ? "#39ff14" : "#9970cc");
  if (active) { ctx.shadowBlur = 16; ctx.shadowColor = "#39ff14"; }
  ctx.fillStyle = fg; ctx.font = "bold 14px 'Courier New', monospace"; ctx.textAlign = "center";
  ctx.fillText(mult + "x", cx, cy + 6);
  ctx.shadowBlur = 0;
}

function drawWinScreen(ctx, won, betAmount, chickenPos) {
  // Semi-transparent overlay
  ctx.fillStyle = "rgba(0,0,0,0.82)";
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  // Gold border box
  const bx = 40, by = 30, bw = CANVAS_W - 80, bh = CANVAS_H - 60;
  drawRoundRect(ctx, bx, by, bw, bh, 16, "#12121f", "#c2a633");
  ctx.strokeStyle = "#c2a633"; ctx.lineWidth = 3; ctx.stroke();
  // StakedApe header
  ctx.font = "28px serif"; ctx.textAlign = "center";
  ctx.fillText("🦧", CANVAS_W / 2, by + 48);
  ctx.fillStyle = "#ffffff"; ctx.font = "bold 11px 'Courier New', monospace";
  ctx.fillText("STAKED", CANVAS_W / 2, by + 68);
  ctx.fillStyle = "#39ff14"; ctx.font = "bold 11px 'Courier New', monospace";
  ctx.fillText("APE", CANVAS_W / 2, by + 84);
  // WIN text
  ctx.shadowBlur = 20; ctx.shadowColor = "#c2a633";
  ctx.fillStyle = "#c2a633"; ctx.font = "bold 28px 'Courier New', monospace";
  ctx.fillText("YOU WIN!", CANVAS_W / 2, by + 128);
  ctx.shadowBlur = 0;
  // Amount
  const mult = LANE_MULTS[chickenPos - 1] || 1;
  const earned = +(betAmount * mult).toFixed(2);
  ctx.fillStyle = "#39ff14"; ctx.font = "bold 20px 'Courier New', monospace";
  ctx.fillText("+" + earned + " DOGE", CANVAS_W / 2, by + 162);
  ctx.fillStyle = "#888"; ctx.font = "10px monospace";
  ctx.fillText(mult + "x multiplier  ·  " + chickenPos + " lanes crossed", CANVAS_W / 2, by + 182);
}

function drawScene(ctx, gameState, chickenPos, lanes, betAmount) {
  const W = CANVAS_W, H = CANVAS_H;
  const roadH = H - SIDEWALK_H * 2;
  const roadY = SIDEWALK_H;

  // ── Sidewalks ───────────────────────────────────────────────
  ctx.fillStyle = "#b0a890";
  ctx.fillRect(0, 0, W, SIDEWALK_H);
  ctx.fillRect(0, roadY + roadH, W, SIDEWALK_H);
  // Sidewalk texture
  ctx.strokeStyle = "#c0b8a0"; ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 20) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, SIDEWALK_H); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, roadY + roadH); ctx.lineTo(x, H); ctx.stroke();
  }

  // ── Road ────────────────────────────────────────────────────
  for (let i = 0; i <= COLS; i++) {
    const lx = i * TILE_W;
    ctx.fillStyle = LANE_COLORS_ALT[i % 2];
    ctx.fillRect(lx, roadY, TILE_W, roadH);
  }

  // Lane dividers — vertical dashed lines between each lane
  for (let i = 1; i <= COLS; i++) {
    const lx = i * TILE_W;
    ctx.strokeStyle = "rgba(255,255,255,0.35)"; ctx.lineWidth = 3;
    ctx.setLineDash([16, 14]);
    ctx.beginPath(); ctx.moveTo(lx, roadY + 6); ctx.lineTo(lx, roadY + roadH - 6); ctx.stroke();
    ctx.setLineDash([]);
  }

  // ── Start column label ──────────────────────────────────────
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.fillRect(0, roadY, TILE_W, roadH);
  ctx.fillStyle = "#ffffff66"; ctx.font = "bold 7px monospace"; ctx.textAlign = "center";
  ctx.fillText("START", TILE_W / 2, roadY + roadH / 2 + 3);

  // ── Lane contents ───────────────────────────────────────────
  const chickMidY = roadY + roadH / 2;
  for (let i = 0; i < COLS; i++) {
    const lx = (i + 1) * TILE_W;
    const lcx = lx + TILE_W / 2;
    const lane = lanes[i];
    const isCrossed = chickenPos > i + 1;
    const isCurrent = chickenPos === i + 1;

    if (!lane) {
      // Not yet revealed
      drawMultTile(ctx, lcx, chickMidY, LANE_MULTS[i], false, false);
      continue;
    }

    if (lane.hasCar) {
      // Danger — draw vehicle (alternate bus/car)
      if (i % 2 === 0) drawTaxi(ctx, lx, roadY, roadH);
      else drawCar(ctx, lx, roadY, roadH);
      // Red tint
      ctx.fillStyle = "rgba(200,0,0,0.2)";
      ctx.fillRect(lx, roadY, TILE_W, roadH);
    } else {
      // Safe — show multiplier tile
      drawMultTile(ctx, lcx, chickMidY, LANE_MULTS[i], true, isCurrent);
    }
  }

  // ── Chicken ─────────────────────────────────────────────────
  const chickX = chickenPos * TILE_W + TILE_W / 2;
  drawChicken(ctx, chickX, chickMidY, gameState === "bust");

  // ── Win overlay ─────────────────────────────────────────────
  if (gameState === "won" && chickenPos > 0) {
    drawWinScreen(ctx, true, betAmount, chickenPos);
  }
}


function drawBarrier(ctx, x, cy) {
  // Yellow/black striped barrier like in the screenshot
  const bw = TILE_W - 10, bh = 18, bx = x + 5, by = cy - bh / 2 - 10;
  // Legs
  ctx.fillStyle = "#888";
  ctx.fillRect(bx + 4, by + bh, 5, 10);
  ctx.fillRect(bx + bw - 9, by + bh, 5, 10);
  // Red lights on top
  ctx.beginPath(); ctx.arc(bx + 6, by + 3, 4, 0, Math.PI * 2);
  ctx.fillStyle = "#ff2222"; ctx.fill();
  ctx.beginPath(); ctx.arc(bx + bw - 6, by + 3, 4, 0, Math.PI * 2);
  ctx.fillStyle = "#ff2222"; ctx.fill();
  // Striped bar
  ctx.save();
  ctx.beginPath(); ctx.rect(bx, by + 4, bw, bh - 4);
  ctx.clip();
  for (let i = 0; i < bw; i += 10) {
    ctx.fillStyle = i % 20 === 0 ? "#ffe600" : "#333";
    ctx.fillRect(bx + i, by + 4, 10, bh - 4);
  }
  ctx.restore();
  ctx.strokeStyle = "#555"; ctx.lineWidth = 1;
  ctx.strokeRect(bx, by + 4, bw, bh - 4);
}

function drawEgg(ctx, x, cy) {
  // Golden egg
  const ex = x + TILE_W / 2, ey = cy;
  ctx.save();
  const grad = ctx.createRadialGradient(ex - 5, ey - 8, 2, ex, ey, 16);
  grad.addColorStop(0, "#fff8cc");
  grad.addColorStop(0.5, "#f5d020");
  grad.addColorStop(1, "#c8960c");
  ctx.beginPath();
  ctx.ellipse(ex, ey, 13, 16, 0, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = "#a07000";
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.restore();
}



function LandingPage({ onPlay }) {
  const [vis, setVis] = useState([]);
  useEffect(() => {
    [100,300,500,700,900,1100,1300].forEach((d,i) => setTimeout(() => setVis(v=>[...v,i]),d));
  }, []);
  const s = (i) => ({ opacity:vis.includes(i)?1:0, transform:vis.includes(i)?"translateY(0)":"translateY(28px)", transition:"all 0.6s cubic-bezier(0.16,1,0.3,1)" });

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:"#fff", fontFamily:"'Chakra Petch','Courier New',sans-serif", overflowX:"hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Chakra+Petch:wght@400;600;700&display=swap" rel="stylesheet" />
      <div style={{ position:"fixed", inset:0, background:`radial-gradient(ellipse at 50% 0%, #1a2a1a 0%, transparent 60%)`, pointerEvents:"none", zIndex:0 }} />
      <div style={{ position:"fixed", inset:0, backgroundImage:`linear-gradient(#ffffff05 1px, transparent 1px), linear-gradient(90deg, #ffffff05 1px, transparent 1px)`, backgroundSize:"60px 60px", pointerEvents:"none", zIndex:0 }} />

      <div style={{ position:"relative", zIndex:1, maxWidth:800, margin:"0 auto", padding:"0 20px" }}>
        {/* Hero */}
        <div style={{ textAlign:"center", paddingTop:80, paddingBottom:60, ...s(0) }}>
          <a href="https://stakedape.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"block", textAlign:"center", marginBottom:12 }}>
            <div style={{ fontSize:40, lineHeight:1 }}>🦧</div>
            <div style={{ fontSize:12, color:"#fff", fontFamily:"'Press Start 2P',monospace", letterSpacing:4, marginTop:6 }}>STAKED <span style={{ color:C.green }}>APE</span></div>
          </a>
          <div style={{ fontSize:72, marginBottom:16 }}>🐔</div>
          <h1 style={{ fontSize:36, margin:0, color:C.green, letterSpacing:6, fontFamily:"'Press Start 2P',monospace", textShadow:`0 0 30px ${C.green}44`, lineHeight:1.3 }}>DOGECROSS</h1>
          <p style={{ fontSize:14, color:C.yellow, marginTop:16, letterSpacing:4, fontWeight:600 }}>CROSS THE ROAD. WIN DOGE.</p>
          <p style={{ fontSize:14, color:"#888", marginTop:20, maxWidth:500, margin:"20px auto 0", lineHeight:1.8 }}>
            Help the chicken cross the road. Each safe lane multiplies your bet — but one car and you lose everything. Cash out anytime!
          </p>
          <div style={{ marginTop:32 }}>
            <button onClick={onPlay} style={{ padding:"18px 48px", fontSize:14, fontFamily:"'Press Start 2P',monospace", color:C.bg, fontWeight:"bold", background:`linear-gradient(135deg, ${C.green}, #66ff44)`, border:"none", borderRadius:12, cursor:"pointer", letterSpacing:4, boxShadow:`0 4px 30px ${C.green}44`, transition:"transform 0.2s" }}
              onMouseEnter={e=>e.target.style.transform="scale(1.05)"} onMouseLeave={e=>e.target.style.transform="scale(1)"}>
              PLAY NOW
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center", marginBottom:60, ...s(1) }}>
          {[["16x","MAX MULT",C.red],["9","LANES",C.yellow],["5%","REFERRAL",C.purple],["DOGE","INSTANT PAY",C.green]].map(([v,l,c])=>(
            <div key={l} style={{ background:"#12121f", border:`1px solid ${c}22`, borderRadius:12, padding:"20px 14px", textAlign:"center", flex:"1 1 130px" }}>
              <div style={{ fontSize:22, color:c, fontFamily:"'Press Start 2P',monospace", marginBottom:8 }}>{v}</div>
              <div style={{ fontSize:8, color:"#888", letterSpacing:2 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Multiplier ladder */}
        <div style={{ marginBottom:60, ...s(2) }}>
          <div style={{ background:"#12121f", border:`1px solid ${C.green}22`, borderRadius:16, padding:32 }}>
            <h2 style={{ fontSize:14, color:C.green, fontFamily:"'Press Start 2P',monospace", marginBottom:20, letterSpacing:3, textAlign:"center" }}>MULTIPLIER LADDER</h2>
            <div style={{ display:"flex", gap:6, justifyContent:"center", flexWrap:"wrap" }}>
              {LANE_MULTS.map((m, i) => (
                <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                  <div style={{ fontSize:8, color:"#555", fontFamily:"monospace" }}>L{i+1}</div>
                  <div style={{ padding:"6px 10px", borderRadius:6, background:`${i < 3 ? C.green : i < 6 ? C.yellow : C.red}15`, border:`1px solid ${i < 3 ? C.green : i < 6 ? C.yellow : C.red}44`, fontSize:10, color:i < 3 ? C.green : i < 6 ? C.yellow : C.red, fontFamily:"'Press Start 2P',monospace" }}>{m}x</div>
                </div>
              ))}
            </div>
            <p style={{ fontSize:12, color:"#666", textAlign:"center", marginTop:16 }}>Each lane you cross multiplies your bet. Cash out before you hit a car!</p>
          </div>
        </div>

        {/* How to play */}
        <div style={{ marginBottom:60, ...s(3) }}>
          <h2 style={{ fontSize:14, color:C.gold, fontFamily:"'Press Start 2P',monospace", textAlign:"center", marginBottom:28, letterSpacing:4 }}>HOW TO PLAY</h2>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {[["1","PLACE YOUR BET","Choose your bet amount and press PLAY. The chicken starts at the curb.","#c2a633"],
              ["2","CROSS THE ROAD","Press CROSS to move one lane forward. Each safe crossing reveals a multiplier.","#39ff14"],
              ["3","CASH OUT ANYTIME","Press CASH OUT to collect your winnings — the further you go, the bigger the reward.","#00f0ff"],
              ["4","AVOID THE CARS","Hit a car and you lose your bet. The road gets more dangerous the further you go!","#ff003c"]
            ].map(([n,t,d,c])=>(
              <div key={n} style={{ background:"#12121f", border:"1px solid #ffffff08", borderRadius:12, padding:20, display:"flex", gap:16, alignItems:"flex-start" }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:`${c}22`, border:`2px solid ${c}44`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Press Start 2P',monospace", fontSize:12, color:c, flexShrink:0 }}>{n}</div>
                <div><div style={{ fontSize:12, color:c, fontWeight:700, marginBottom:6, fontFamily:"'Chakra Petch',sans-serif" }}>{t}</div><div style={{ fontSize:12, color:"#888", lineHeight:1.7 }}>{d}</div></div>
              </div>
            ))}
          </div>
        </div>

        {/* Referral */}
        <div style={{ marginBottom:60, ...s(4) }}>
          <h2 style={{ fontSize:14, color:C.purple, fontFamily:"'Press Start 2P',monospace", textAlign:"center", marginBottom:28, letterSpacing:4 }}>EARN PASSIVE DOGE</h2>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:16 }}>
            {[["🔗","SHARE YOUR LINK",C.purple,"Every player gets a unique referral link."],
              ["💰","5% DEPOSIT BONUS",C.green,"Earn 5% of your referral's first deposit."],
              ["♾️","2% FOREVER",C.cyan,"Earn 2% of every bet they make, forever."]
            ].map(([icon,t,c,d])=>(
              <div key={t} style={{ background:"#12121f", border:`1px solid ${c}22`, borderRadius:12, padding:24, textAlign:"center" }}>
                <div style={{ fontSize:28, marginBottom:12 }}>{icon}</div>
                <div style={{ fontSize:11, color:c, fontWeight:700, marginBottom:8 }}>{t}</div>
                <div style={{ fontSize:12, color:"#888", lineHeight:1.7 }}>{d}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign:"center", paddingBottom:80, ...s(5) }}>
          <div style={{ background:`linear-gradient(135deg, ${C.green}11, ${C.panel})`, border:`1px solid ${C.green}22`, borderRadius:20, padding:"40px 24px" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🐔</div>
            <h2 style={{ fontSize:14, color:C.green, fontFamily:"'Press Start 2P',monospace", marginBottom:12, letterSpacing:3 }}>READY TO CROSS?</h2>
            <p style={{ fontSize:13, color:"#888", marginBottom:24, lineHeight:1.7 }}>Connect your DOGE wallet and help the chicken cross the road.</p>
            <button onClick={onPlay} style={{ padding:"16px 40px", fontSize:13, fontFamily:"'Press Start 2P',monospace", color:C.bg, background:`linear-gradient(135deg, ${C.green}, #66ff44)`, border:"none", borderRadius:10, cursor:"pointer", letterSpacing:4, boxShadow:`0 4px 30px ${C.green}44` }}>START CROSSING</button>
          </div>
        </div>
        <div style={{ textAlign:"center", paddingBottom:30 }}>
          <a href="https://stakedape.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:8 }}>
            <span style={{ fontSize:18 }}>🦧</span>
            <span style={{ fontSize:9, color:"#fff", fontFamily:"'Press Start 2P',monospace", letterSpacing:3 }}>STAKED <span style={{ color:C.green }}>APE</span></span>
          </a>
          <div style={{ fontSize:7, color:"#444", letterSpacing:2 }}>
            <a href="https://www.dogedropz.com" style={{ color:"#444", textDecoration:"none", marginRight:8 }}>DOGEDROPZ</a>·
            <a href="https://www.dogebrix.com" style={{ color:"#444", textDecoration:"none", margin:"0 8px" }}>DOGEBRIX</a>·
            <a href="https://www.dogegemz.com" style={{ color:"#444", textDecoration:"none", marginLeft:8 }}>DOGEGEMZ</a>
          </div>
          <div style={{ fontSize:5, color:"#333", marginTop:4 }}>for entertainment purposes only</div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CONNECT SCREEN
// ============================================================
function ConnectScreen({ onConnect, onBack }) {
  const [step, setStep] = useState("address");
  const [address, setAddress] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const checkAddr = async () => {
    const t = address.trim();
    if (!t || t.length < 20) { setError("Enter a valid Dogecoin address"); return; }
    setLoading(true); setError("");
    try { const d = await api("/api/auth/check", { method: "POST", body: { walletAddress: t } }); setIsNew(!d.exists || !d.hasPin); setStep("pin"); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  const connect = async () => {
    if (!pin || pin.length < 6) { setError("PIN must be at least 6 characters"); return; }
    if (isNew && pin !== pinConfirm) { setError("PINs don't match"); return; }
    setLoading(true); setError("");
    try {
      const d = await api("/api/auth/connect", { method: "POST", body: { walletAddress: address.trim(), pin } });
      localStorage.setItem("dc_token", d.token);
      onConnect(d.user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inp = { width: "100%", padding: "12px 14px", background: "#0a0a16", border: `1px solid ${error ? C.red : "#333"}`, borderRadius: 6, color: "#fff", fontFamily: "monospace", fontSize: 11, boxSizing: "border-box", outline: "none" };

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 50% 0%, #1a2a1a 0%, ${C.bg} 70%)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Press Start 2P', monospace", color: "#fff", padding: 20 }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap" rel="stylesheet" />
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        <a href="https://stakedape.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"block", textAlign:"center", marginBottom:10 }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
            <span style={{ fontSize:44, lineHeight:1 }}>🦧</span>
            <div style={{ fontSize:10, color:"#fff", fontFamily:"'Press Start 2P',monospace", letterSpacing:4, marginTop:4 }}>STAKED <span style={{ color:C.green }}>APE</span></div>
          </div>
        </a>
        <div style={{ fontSize: 40, marginBottom: 10 }}>🐔</div>
        <h1 style={{ fontSize: 22, margin: 0, color: C.green, letterSpacing: 6, textShadow: `0 0 20px ${C.green}66` }}>DOGECROSS</h1>
        <div style={{ fontSize: 8, color: C.yellow, marginTop: 8, letterSpacing: 4 }}>CROSS THE ROAD. WIN DOGE.</div>
      </div>
      <div style={{ background: C.panel, border: `1px solid ${C.green}33`, borderRadius: 16, padding: 28, width: "100%", maxWidth: 400 }}>
        {step === "address" ? (
          <>
            <div style={{ fontSize: 9, color: C.green, marginBottom: 14, textAlign: "center", letterSpacing: 2 }}>CONNECT WALLET</div>
            <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Your DOGE address..." style={inp} onKeyDown={e => e.key === "Enter" && checkAddr()} />
            {error && <div style={{ fontSize: 7, color: C.red, marginTop: 6 }}>{error}</div>}
            <button onClick={checkAddr} disabled={loading} style={{ width: "100%", marginTop: 14, padding: "13px 0", background: `linear-gradient(135deg, ${C.green}, #66ff44)`, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 10, color: C.bg, fontWeight: "bold", letterSpacing: 3 }}>{loading ? "CHECKING..." : "CONTINUE"}</button>
            <button onClick={onBack} style={{ width: "100%", marginTop: 8, padding: "9px 0", background: "transparent", border: "1px solid #333", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 8, color: "#555" }}>BACK TO HOME</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 9, color: C.green, marginBottom: 12, textAlign: "center" }}>{isNew ? "CREATE PIN" : "ENTER PIN"}</div>
            <div style={{ fontSize: 7, color: C.green, fontFamily: "monospace", marginBottom: 12, textAlign: "center" }}>{address.slice(0, 8)}...{address.slice(-6)}</div>
            <input type="password" value={pin} onChange={e => setPin(e.target.value)} placeholder="PIN (6+ chars)" style={{ ...inp, marginBottom: isNew ? 10 : 0 }} onKeyDown={e => e.key === "Enter" && !isNew && connect()} />
            {isNew && <input type="password" value={pinConfirm} onChange={e => setPinConfirm(e.target.value)} placeholder="Confirm PIN" style={inp} onKeyDown={e => e.key === "Enter" && connect()} />}
            {error && <div style={{ fontSize: 7, color: C.red, marginTop: 6 }}>{error}</div>}
            <button onClick={connect} disabled={loading} style={{ width: "100%", marginTop: 14, padding: "13px 0", background: `linear-gradient(135deg, ${C.green}, #66ff44)`, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 10, color: C.bg, fontWeight: "bold", letterSpacing: 3 }}>{loading ? "CONNECTING..." : "CONNECT"}</button>
            <button onClick={() => { setStep("address"); setPin(""); setPinConfirm(""); setError(""); }} style={{ width: "100%", marginTop: 8, padding: "9px 0", background: "transparent", border: "1px solid #333", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 8, color: "#555" }}>BACK</button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// WALLET PANEL
// ============================================================
function WalletPanel({ user, balance, onBalanceUpdate, onClose, onDisconnect, houseWallet }) {
  const [tab, setTab] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [copied, setCopied] = useState(false);
  const [refCopied, setRefCopied] = useState(false);
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [refStats, setRefStats] = useState(null);

  useEffect(() => { api("/api/referral/stats").then(setRefStats).catch(() => {}); }, []);

  const copyAddr = () => { navigator.clipboard.writeText(houseWallet); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const copyRef = () => { navigator.clipboard.writeText(`${SITE_URL}?ref=${user.referralCode}`); setRefCopied(true); setTimeout(() => setRefCopied(false), 2000); };

  const checkDeposits = async () => {
    setChecking(true); setStatus({ t: "i", m: "Checking blockchain..." });
    try {
      const d = await api("/api/wallet/deposit/check", { method: "POST" });
      onBalanceUpdate(d.balance);
      setStatus(d.credited > 0 ? { t: "s", m: `${d.credited.toFixed(2)} DOGE credited!` } : { t: "i", m: "No new deposits found." });
    } catch (e) { setStatus({ t: "e", m: e.message }); }
    setChecking(false);
  };

  const withdraw = async () => {
    const v = parseFloat(amount);
    if (isNaN(v) || v < 10) { setStatus({ t: "e", m: "Minimum: 10 DOGE" }); return; }
    try {
      const d = await api("/api/wallet/withdraw", { method: "POST", body: { amount: v } });
      onBalanceUpdate(d.balance);
      setStatus({ t: "s", m: `${v} DOGE withdrawal submitted` });
      setAmount("");
    } catch (e) { setStatus({ t: "e", m: e.message }); }
  };

  const tabBtn = (active) => ({ flex: 1, padding: "9px 0", textAlign: "center", cursor: "pointer", fontSize: 7, fontFamily: "inherit", letterSpacing: 2, border: "none", background: active ? C.green + "22" : "transparent", borderBottom: active ? `2px solid ${C.green}` : "2px solid transparent", color: active ? C.green : "#555" });
  const inp = { width: "100%", padding: "9px 12px", background: "#0a0a16", border: "1px solid #333", borderRadius: 4, color: "#fff", fontFamily: "monospace", fontSize: 10, boxSizing: "border-box", outline: "none" };
  const stColor = status?.t === "s" ? C.green : status?.t === "e" ? C.red : C.cyan;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000000cc", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.panel, border: `1px solid ${C.green}33`, borderRadius: 16, width: "100%", maxWidth: 420, maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #ffffff11" }}>
          <Glow color={C.green} size={10}>WALLET</Glow>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #ffffff08", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 6, color: "#444", marginBottom: 2 }}>{user.verified ? "✅ VERIFIED" : "⚠️ UNVERIFIED"}</div>
            <div style={{ fontSize: 7, color: C.green, fontFamily: "monospace" }}>{user.walletAddress.slice(0, 8)}...{user.walletAddress.slice(-6)}</div>
          </div>
          <button onClick={onDisconnect} style={{ padding: "5px 10px", fontSize: 6, fontFamily: "inherit", background: C.red + "11", border: `1px solid ${C.red}33`, color: C.red, borderRadius: 4, cursor: "pointer" }}>DISCONNECT</button>
        </div>
        <div style={{ padding: "12px 16px", textAlign: "center", borderBottom: "1px solid #ffffff08" }}>
          <div style={{ fontSize: 6, color: "#555", marginBottom: 4 }}>BALANCE</div>
          <Glow color={C.gold} size={18}>{balance.toFixed(2)} DOGE</Glow>
        </div>
        <div style={{ display: "flex", borderBottom: "1px solid #ffffff11" }}>
          <button onClick={() => { setTab("deposit"); setStatus(null); }} style={tabBtn(tab === "deposit")}>DEPOSIT</button>
          <button onClick={() => { setTab("withdraw"); setStatus(null); }} style={tabBtn(tab === "withdraw")}>WITHDRAW</button>
          <button onClick={() => { setTab("referral"); setStatus(null); }} style={tabBtn(tab === "referral")}>REFERRAL</button>
        </div>
        <div style={{ padding: 16 }}>
          {tab === "deposit" && (
            <>
              <div style={{ background: "#0a0a16", border: `1px solid ${C.gold}33`, borderRadius: 8, padding: 12, marginBottom: 12, wordBreak: "break-all" }}>
                <div style={{ fontSize: 6, color: "#555", marginBottom: 6 }}>SEND DOGE TO</div>
                <div style={{ fontSize: 7, color: C.goldLt, lineHeight: 1.6, marginBottom: 8 }}>{houseWallet}</div>
                <button onClick={copyAddr} style={{ padding: "6px 14px", fontSize: 7, fontFamily: "inherit", borderRadius: 4, cursor: "pointer", background: copied ? C.green + "22" : C.gold + "22", border: `1px solid ${copied ? C.green : C.gold}44`, color: copied ? C.green : C.gold }}>{copied ? "COPIED ✓" : "COPY ADDRESS"}</button>
              </div>
              <button onClick={checkDeposits} disabled={checking} style={{ width: "100%", padding: "11px 0", background: checking ? "#333" : `linear-gradient(135deg, ${C.cyan}, ${C.green})`, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 9, color: C.bg, fontWeight: "bold", letterSpacing: 2 }}>{checking ? "CHECKING..." : "CHECK FOR DEPOSITS"}</button>
            </>
          )}
          {tab === "withdraw" && (
            <>
              {!user.verified && <div style={{ fontSize: 7, color: C.red, marginBottom: 8 }}>Deposit first to enable withdrawals.</div>}
              <div style={{ fontSize: 7, color: "#888", marginBottom: 4 }}>Sends to your wallet address</div>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount (min 10)" style={inp} />
              <button onClick={withdraw} disabled={!user.verified} style={{ width: "100%", marginTop: 10, padding: "11px 0", background: user.verified ? `linear-gradient(135deg, ${C.cyan}, ${C.green})` : "#333", border: "none", borderRadius: 8, cursor: user.verified ? "pointer" : "not-allowed", fontFamily: "inherit", fontSize: 9, color: C.bg, fontWeight: "bold", letterSpacing: 2 }}>WITHDRAW</button>
            </>
          )}
          {tab === "referral" && (
            <>
              <div style={{ fontSize: 7, color: "#888", marginBottom: 12, lineHeight: 1.8 }}>Share your link. Earn 5% of their first deposit + 2% of every bet.</div>
              <div style={{ background: "#0a0a16", border: `1px solid ${C.purple}33`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 6, color: "#555", marginBottom: 6 }}>YOUR REFERRAL LINK</div>
                <div style={{ fontSize: 7, color: C.purple, lineHeight: 1.6, marginBottom: 8, wordBreak: "break-all" }}>{SITE_URL}?ref={user.referralCode}</div>
                <button onClick={copyRef} style={{ padding: "6px 14px", fontSize: 7, fontFamily: "inherit", borderRadius: 4, cursor: "pointer", background: refCopied ? C.green + "22" : C.purple + "22", border: `1px solid ${refCopied ? C.green : C.purple}44`, color: refCopied ? C.green : C.purple }}>{refCopied ? "COPIED ✓" : "COPY LINK"}</button>
              </div>
              {refStats && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                  <div style={{ background: "#0a0a16", borderRadius: 6, padding: 10, textAlign: "center" }}><div style={{ fontSize: 6, color: "#555", marginBottom: 4 }}>REFERRALS</div><Glow color={C.cyan} size={14}>{refStats.referrals}</Glow></div>
                  <div style={{ background: "#0a0a16", borderRadius: 6, padding: 10, textAlign: "center" }}><div style={{ fontSize: 6, color: "#555", marginBottom: 4 }}>EARNED</div><Glow color={C.green} size={14}>{refStats.totalEarned.toFixed(2)}</Glow></div>
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I just won DOGE playing DOGECROSS! Help the chicken cross the road. ${SITE_URL}?ref=${user.referralCode}`)}`, "_blank")} style={{ flex: 1, padding: "10px 0", fontSize: 7, fontFamily: "inherit", background: "#1DA1F222", border: "1px solid #1DA1F244", color: "#1DA1F2", borderRadius: 6, cursor: "pointer" }}>SHARE ON X</button>
                <button onClick={() => window.open(`https://t.me/share/url?url=${encodeURIComponent(`${SITE_URL}?ref=${user.referralCode}`)}&text=${encodeURIComponent("I just won DOGE playing DOGECROSS! Help the chicken cross the road.")}`, "_blank")} style={{ flex: 1, padding: "10px 0", fontSize: 7, fontFamily: "inherit", background: "#0088cc22", border: "1px solid #0088cc44", color: "#0088cc", borderRadius: 6, cursor: "pointer" }}>TELEGRAM</button>
              </div>
            </>
          )}
          {status && <div style={{ marginTop: 10, padding: 8, borderRadius: 4, textAlign: "center", fontSize: 7, background: stColor + "11", border: `1px solid ${stColor}33`, color: stColor }}>{status.m}</div>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN GAME
// ============================================================
export default function App() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  const [screen, setScreen] = useState("loading");
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [betAmount, setBetAmount] = useState(10);
  const [gameState, setGameState] = useState("idle"); // idle | playing | bust | won
  const [chickenPos, setChickenPos] = useState(0);    // 0 = start, 1-9 = lane crossed
  const [lanes, setLanes] = useState([]);              // revealed lane data from server
  const [gameId, setGameId] = useState(null);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [walletOpen, setWalletOpen] = useState(false);
  const [houseWallet, setHouseWallet] = useState("");
  const [crossing, setCrossing] = useState(false);    // prevent double clicks

  const betRef = useRef(betAmount);
  useEffect(() => { betRef.current = betAmount; }, [betAmount]);

  // ── Auth init ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try { const i = await api("/api/info"); setHouseWallet(i.houseWallet); } catch {}
      const t = localStorage.getItem("dc_token");
      if (t) {
        try { const u = await api("/api/auth/me"); setUser(u); setBalance(u.balance); setScreen("game"); return; }
        catch { localStorage.removeItem("dc_token"); }
      }
      setScreen("landing");
    })();
  }, []);

  const handleConnect = (u) => { setUser(u); setBalance(u.balance); setScreen("game"); };
  const handleDisconnect = () => { localStorage.removeItem("dc_token"); setUser(null); setBalance(0); setScreen("landing"); };

  // ── Canvas draw loop ─────────────────────────────────────
  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    drawScene(ctx, gameState, chickenPos, lanes, betRef.current);
  }, [gameState, chickenPos, lanes]);

  // ── Start game ───────────────────────────────────────────
  const startGame = useCallback(async () => {
    const bet = betRef.current;
    if (bet > balance) { setMessage("Insufficient balance"); return; }
    setMessage(""); setCrossing(false);
    try {
      const d = await api("/api/cross/start", { method: "POST", body: { betAmount: bet } });
      setBalance(d.balance);
      setGameId(d.gameId);
      setChickenPos(0);
      setLanes(new Array(COLS).fill(null));
      setGameState("playing");
      SFX.gameStart();
    } catch (e) { setMessage(e.message); }
  }, [balance]);

  // ── Cross one lane ────────────────────────────────────────
  const crossLane = useCallback(async () => {
    if (crossing || gameState !== "playing" || chickenPos >= COLS) return;
    setCrossing(true);
    const nextPos = chickenPos + 1;
    try {
      const d = await api("/api/cross/step", { method: "POST", body: { gameId, laneIndex: nextPos - 1 } });
      const newLanes = [...lanes];
      newLanes[nextPos - 1] = { revealed: true, hasCar: d.hasCar };
      setLanes(newLanes);

      if (d.hasCar) {
        // Bust!
        SFX.honk();
        setTimeout(() => SFX.bust(), 150);
        setChickenPos(nextPos);
        setGameState("bust");
        setBalance(d.balance);
        setMessage("🚗 SPLAT! Bet lost!");
        setHistory(h => [{ won: false, amount: betRef.current, lanes: chickenPos, time: Date.now() }, ...h].slice(0, 20));
      } else {
        SFX.step();
        setTimeout(() => SFX.safe(), 100);
        setChickenPos(nextPos);
        setCrossing(false);
        const mult = LANE_MULTS[nextPos - 1];
        setMessage(`Lane ${nextPos} safe! ${mult}x — ${(betRef.current * mult).toFixed(2)} DOGE`);
        if (nextPos >= COLS) {
          // Crossed all lanes — auto cash out
          await cashOut(nextPos, d);
        }
      }
    } catch (e) { setMessage(e.message); setCrossing(false); }
  }, [crossing, gameState, chickenPos, gameId, lanes]);

  // ── Cash out ─────────────────────────────────────────────
  const cashOut = useCallback(async (pos, stepData) => {
    const currentPos = pos ?? chickenPos;
    if (currentPos === 0) return;
    if (gameState !== "playing") return;
    try {
      const d = stepData ?? await api("/api/cross/cashout", { method: "POST", body: { gameId } });
      setBalance(d.balance);
      const mult = LANE_MULTS[currentPos - 1];
      const won = +(betRef.current * mult).toFixed(4);
      setGameState("won");
      SFX.cashOut();
      setMessage(`✅ Cashed out ${won.toFixed(2)} DOGE! (${mult}x)`);
      setHistory(h => [{ won: true, amount: won, lanes: currentPos, time: Date.now() }, ...h].slice(0, 20));
    } catch (e) { setMessage(e.message); }
  }, [gameState, chickenPos, gameId]);

  // ── Spacebar ─────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.key === " ") {
        e.preventDefault();
        if (gameState === "playing" && chickenPos > 0) cashOut();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gameState, chickenPos, cashOut]);

  // ── Render ───────────────────────────────────────────────
  if (screen === "loading") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Press Start 2P', monospace", color: C.green }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap" rel="stylesheet" />
      Loading...
    </div>
  );
  if (screen === "landing") return <LandingPage onPlay={() => setScreen("connect")} />;
  if (screen === "connect") return <ConnectScreen onConnect={handleConnect} onBack={() => setScreen("landing")} />;

  const bets = [10, 25, 50, 100, 250, 500, 1000];
  const canPlay = balance >= betAmount && balance > 0;
  const currentMult = chickenPos > 0 ? LANE_MULTS[chickenPos - 1] : null;
  const currentEarned = currentMult ? +(betAmount * currentMult).toFixed(2) : 0;

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 50% 0%, #0d1a0d 0%, ${C.bg} 70%)`, display: "flex", flexDirection: "column", alignItems: "center", fontFamily: "'Press Start 2P', 'Courier New', monospace", color: "#fff", padding: "12px 8px" }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Chakra+Petch:wght@400;600;700&display=swap" rel="stylesheet" />

      {walletOpen && <WalletPanel user={user} balance={balance} onBalanceUpdate={setBalance} onClose={() => setWalletOpen(false)} onDisconnect={handleDisconnect} houseWallet={houseWallet} />}

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 10 }}>
        {/* StakedApe Header */}
      <a href="https://stakedape.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"block", textAlign:"center", marginBottom:6 }}>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
          <span style={{ fontSize:44, lineHeight:1 }}>🦧</span>
          <div style={{ fontSize:11, color:"#fff", fontFamily:"'Press Start 2P',monospace", letterSpacing:4, marginTop:4 }}>STAKED <span style={{ color:"#39ff14" }}>APE</span></div>
        </div>
      </a>
        <h1 style={{ fontSize: 20, margin: "2px 0 0", color: C.green, letterSpacing: 6, textShadow: `0 0 20px ${C.green}66` }}>DOGECROSS</h1>
        <div style={{ fontSize: 7, color: C.yellow, marginTop: 4, letterSpacing: 3 }}>CROSS THE ROAD. WIN DOGE.</div>
      </div>

      {/* Status bar */}
      <div style={{ background: C.panel, border: `1px solid ${C.green}33`, borderRadius: 10, padding: "8px 12px", marginBottom: 8, display: "flex", gap: 10, alignItems: "center", width: CANVAS_W, maxWidth: "100%", boxSizing: "border-box", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div><div style={{ fontSize: 5, color: "#444", marginBottom: 2 }}>WALLET</div><div style={{ fontSize: 7, color: C.green, fontFamily: "monospace" }}>{user.walletAddress.slice(0, 6)}...{user.walletAddress.slice(-4)}</div></div>
          <div style={{ width: 1, height: 28, background: "#333" }} />
          <div><div style={{ fontSize: 5, color: "#444", marginBottom: 2 }}>BALANCE</div><Glow color={C.gold} size={10}>{balance.toFixed(2)}</Glow></div>
          {gameState === "playing" && chickenPos > 0 && <>
            <div style={{ width: 1, height: 28, background: "#333" }} />
            <div><div style={{ fontSize: 5, color: "#444", marginBottom: 2 }}>IF CASH OUT</div><Glow color={C.green} size={10}>{currentEarned.toFixed(2)}</Glow></div>
          </>}
        </div>
        <button onClick={() => setWalletOpen(true)} style={{ padding: "8px 12px", fontSize: 7, fontFamily: "inherit", background: C.green + "22", border: `1px solid ${C.green}66`, color: C.green, borderRadius: 6, cursor: "pointer" }}>WALLET</button>
      </div>

      {/* Canvas */}
      <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H}
        style={{ border: `1px solid ${C.green}44`, borderRadius: 8, boxShadow: `0 0 40px ${C.green}11`, width: "100%", display: "block" }}
      />

      {/* Message */}
      {message && (
        <div style={{ marginTop: 6, padding: "5px 16px", borderRadius: 6, textAlign: "center", fontSize: 9,
          color: message.startsWith("✅") ? C.green : message.startsWith("🚗") ? C.red : C.cyan,
          background: "#ffffff08", border: "1px solid #ffffff11", width: CANVAS_W, maxWidth: "100%", boxSizing: "border-box" }}>
          {message}
        </div>
      )}

      {/* Controls */}
      <div style={{ background: C.panel, border: "1px solid #ffffff11", borderRadius: 10, padding: 10, marginTop: 8, width: CANVAS_W, maxWidth: "100%", boxSizing: "border-box" }}>
        {gameState === "idle" || gameState === "bust" || gameState === "won" ? (
          <>
            <div style={{ fontSize: 7, color: "#666", marginBottom: 6 }}>BET (DOGE)</div>
            <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
              {bets.map(b => (
                <button key={b} onClick={() => setBetAmount(b)}
                  style={{ background: betAmount === b ? C.green + "33" : "#1a1a2e", border: `1px solid ${betAmount === b ? C.green : "#333"}`, color: betAmount === b ? C.green : "#888", padding: "6px 10px", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", fontSize: 8 }}>{b}</button>
              ))}
            </div>
            <button onClick={startGame} disabled={!canPlay}
              style={{ width: "100%", padding: "14px 0", background: canPlay ? `linear-gradient(135deg, ${C.green}, #66ff44)` : "#333", border: "none", borderRadius: 8, cursor: canPlay ? "pointer" : "not-allowed", fontFamily: "inherit", fontSize: 12, color: C.bg, fontWeight: "bold", letterSpacing: 4, boxShadow: canPlay ? `0 4px 20px ${C.green}44` : "none" }}>
              {balance === 0 ? "DEPOSIT TO PLAY" : gameState === "bust" ? "PLAY AGAIN" : gameState === "won" ? "PLAY AGAIN" : "PLAY"}
            </button>
          </>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={crossLane} disabled={crossing || chickenPos >= COLS}
              style={{ flex: 1, padding: "14px 0", background: crossing ? "#333" : `linear-gradient(135deg, ${C.yellow}, ${C.orange})`, border: "none", borderRadius: 8, cursor: crossing ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 11, color: C.bg, fontWeight: "bold", letterSpacing: 2, boxShadow: `0 4px 20px ${C.yellow}44` }}>
              {crossing ? "..." : "CROSS 🐔"}
            </button>
            <button onClick={() => cashOut()} disabled={chickenPos === 0}
              style={{ flex: 1, padding: "14px 0", background: chickenPos > 0 ? `linear-gradient(135deg, ${C.green}, ${C.cyan})` : "#333", border: "none", borderRadius: 8, cursor: chickenPos > 0 ? "pointer" : "not-allowed", fontFamily: "inherit", fontSize: 11, color: C.bg, fontWeight: "bold", letterSpacing: 2, boxShadow: chickenPos > 0 ? `0 4px 20px ${C.green}44` : "none" }}>
              CASH OUT
            </button>
          </div>
        )}
        {gameState === "playing" && chickenPos > 0 && (
          <div style={{ textAlign: "center", fontSize: 6, color: "#555", marginTop: 4 }}>or press SPACEBAR to cash out</div>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div style={{ background: C.panel, border: "1px solid #ffffff11", borderRadius: 10, padding: 10, marginTop: 8, width: CANVAS_W, maxWidth: "100%", boxSizing: "border-box" }}>
          <div style={{ fontSize: 7, color: "#666", marginBottom: 6 }}>HISTORY</div>
          {history.slice(0, 8).map((h, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #1a1a2e", fontSize: 7 }}>
              <span style={{ color: "#555" }}>{h.lanes} lane{h.lanes !== 1 ? "s" : ""}</span>
              <span style={{ color: h.won ? C.green : C.red }}>{h.won ? `+${h.amount.toFixed(2)}` : `-${h.amount.toFixed(2)}`} DOGE</span>
            </div>
          ))}
        </div>
      )}

      {/* StakedApe Footer */}
      <a href="https://stakedape.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginTop:16 }}>
        <span style={{ fontSize:18 }}>🦧</span>
        <span style={{ fontSize:9, color:"#fff", fontFamily:"'Press Start 2P',monospace", letterSpacing:3 }}>STAKED <span style={{ color:"#39ff14" }}>APE</span></span>
      </a>
      <div style={{ fontSize:7, color:"#444", marginTop:6, letterSpacing:2, textAlign:"center" }}>
        <a href="https://www.dogedropz.com" style={{ color:"#444", textDecoration:"none", marginRight:8 }}>DOGEDROPZ</a>·
        <a href="https://www.dogebrix.com" style={{ color:"#444", textDecoration:"none", margin:"0 8px" }}>DOGEBRIX</a>·
        <a href="https://www.dogegemz.com" style={{ color:"#444", textDecoration:"none", marginLeft:8 }}>DOGEGEMZ</a>
      </div>
      <div style={{ fontSize:5, color:"#333", marginTop:4, textAlign:"center" }}>for entertainment purposes only</div>
    </div>
  );
}
